import { useCallback, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { flushSync } from 'react-dom';

import toast from 'components/partials/toast/toast';

import {
  buildPngExportFileName,
  captureElementToPng,
  savePngDataUrl,
  waitForFontsReady,
} from 'helpers/png-export';

const DEFAULT_FILE_NAME = 'export';

interface UseElementPngExportArgs {
  targetRef: RefObject<HTMLElement | null>;
  fileName?: string;
}

export interface UseElementPngExportResult {
  exportPng: () => Promise<boolean>;
  isExporting: boolean;
}

const notifyExportFailed = (message: string): void => {
  toast.error({ title: 'Export Failed', message });
};

export const useElementPngExport = ({
  targetRef,
  fileName = DEFAULT_FILE_NAME,
}: UseElementPngExportArgs): UseElementPngExportResult => {
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const isExportingRef = useRef(false);

  const exportPng = useCallback(async (): Promise<boolean> => {
    if (isExportingRef.current) return false;

    isExportingRef.current = true;
    flushSync(() => setIsExporting(true));

    try {
      const element = targetRef.current;

      if (!element) {
        notifyExportFailed('Content not found. Please try again.');

        return false;
      }

      await waitForFontsReady();

      const dataUrl = await captureElementToPng(element);

      await savePngDataUrl(
        dataUrl,
        buildPngExportFileName(fileName, DEFAULT_FILE_NAME)
      );

      return true;
    } catch (error) {
      console.error('Error exporting PNG:', error);
      notifyExportFailed('An error occurred while exporting the PNG.');

      return false;
    } finally {
      isExportingRef.current = false;
      setIsExporting(false);
    }
  }, [fileName, targetRef]);

  return { exportPng, isExporting };
};
